import { Injectable } from '@angular/core';
import type { VariantLabel } from '../_pages/inventory/barcode-labels/variant-label';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

// POST /product/inventory (paginated/filtered list) and
// GET /product/inventory/categories, added in product.controller.ts /
// product.service.ts specifically for the back-office Inventory page -
// see getInventory / getInventoryCategories / updateInventoryVariant there
// for exactly what each field maps to.
export interface InventoryApiRow {
  productId: number;
  productName: string;
  category: string | null;
  productVariantId: number;
  sku: string;
  barcode: string | null;
  // The variant's attribute values (e.g. "Dark Brown"), to tell it from its siblings.
  label: string;
  image: string | null;
  stockQty: number;
  kStockQty: number;
  mStockQty: number;
  price: number;
  // null means "not on sale" (the backend maps discPrice <= 0 to null).
  discPrice: number | null;
  // What we pay the supplier - only sent to super admins and the purchasing
  // team (absent for everyone else); null for most rows today.
  costPrice?: number | null;
  // Price per m² and approx coverage (m² per pack/unit) - null when not set.
  priceMS: number | null;
  approxCoverage: number | null;
  // For "View on website"; productActive false = hidden on the site.
  productSlug?: string | null;
  productActive?: boolean | null;
  // "Extreme Products": another variant of the same product this one links to.
  extremeProductVariantId?: number | null;
}

export interface InventoryQuery {
  pageIndex: number;
  pageSize: number;
  search?: string;
  category?: string;
  store?: string;
  status?: string;
  onSale?: boolean;
  // 'name' (default) | 'stockAsc' | 'stockDesc' | 'priceAsc' | 'priceDesc'
  sort?: string;
  // One row per variant (default), or a page of products with their variants beneath.
  groupByProduct?: boolean;
  // Just this one item (the inventory edit page).
  variantId?: number;
}

export interface InventoryQueryResult {
  items: InventoryApiRow[];
  // Products on this result - the page is a page of products, each with its
  // matching variants; variantCount is the items under them all.
  count: number;
  variantCount: number;
  // True when `count` (and the paging) is in products rather than items.
  grouped: boolean;
  canSeeCost: boolean;
  // Only when one item is asked for (variantId): the product's other variants.
  variantOptions?: { productVariantId: number; sku: string; label: string }[];
  // Aggregates over the whole filtered result, not just `items` (the
  // current page) - see getInventory's `summary` query in
  // product.service.ts.
  unitsOnHand: number;
  // Units x regular price, for real stock only (made-to-order placeholders are left out).
  stockValue: number;
  lowStockCount: number;
  outOfStockCount: number;
  // For the status chips: the current search / category / store, whatever status is chosen.
  statusCounts: { all: number; in: number; low: number; out: number };
}

export interface InventoryVariantUpdate {
  kStockQty?: number;
  mStockQty?: number;
  price?: number;
  discPrice?: number | null;
  costPrice?: number | null;
  // Why the change was made - kept in the item's stock history.
  reason?: string;
  // Another variant of the same product; null clears it.
  extremeProductVariantId?: number | null;
}

export interface StockMovementRow {
  id: string;
  warehouse: string;
  quantityChange: number;
  balanceAfter: number;
  totalAfter: number;
  movementType: string;
  referenceType: string | null;
  referenceId: number | null;
  referenceLabel: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface StockHistoryFilters {
  warehouse?: string; // 'Kettering' | 'Markstey'
  direction?: string; // 'in' | 'out'
  group?: string; // 'orders' | 'purchases' | 'manual' | 'scrapReturns' | 'transfers'
}

export interface StockHistoryResult {
  items: StockMovementRow[];
  count: number;
  // Live stock vs what the recorded movements add up to - a difference
  // means a stock change happened that never got recorded.
  actual: { Kettering: number; Markstey: number; total: number };
  ledger: { Kettering: number; Markstey: number };
}

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  constructor(private http: HttpClient) {}

  // Details for the crate ("Variant Barcode") labels of these items.
  getVariantLabels(variantIds: number[]): Observable<VariantLabel[]> {
    return this.http.post<VariantLabel[]>('/product/inventory/variant-labels', { variantIds });
  }

  getInventory(query: InventoryQuery): Observable<InventoryQueryResult> {
    return this.http.post<InventoryQueryResult>('/product/inventory', query);
  }

  getStockHistory(
    productVariantId: number,
    pageIndex: number,
    pageSize: number,
    filters: StockHistoryFilters = {},
  ): Observable<StockHistoryResult> {
    return this.http.post<StockHistoryResult>(`/stock-movements/variant/${productVariantId}`, { pageIndex, pageSize, ...filters });
  }

  getCategories(): Observable<string[]> {
    return this.http.get<string[]>('/product/inventory/categories');
  }

  updateVariant(productVariantId: number, update: InventoryVariantUpdate): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(`/product/inventory/${productVariantId}`, update);
  }
}
